import React, { useState, useEffect } from "react";
import { UsersIcon, ExternalLinkIcon } from "lucide-react";
import { Button } from "../ui/button";
import { FriendRecommendationItem } from "./FriendRecommendationItem";
import { FriendRequestItem } from "./FriendRequestItem";
import {
  getFriendRecommendations,
  getPendingFriendRequests,
  getSentFriendRequests,
  UserProfile,
  Friend,
} from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { SearchPeopleDialog } from "./SearchPeopleDialog";

interface FriendRecommendationsProps {
  onFriendAccepted?: () => void;
}

export const FriendRecommendations: React.FC<FriendRecommendationsProps> = ({
  onFriendAccepted,
}) => {
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState<UserProfile[]>([]);
  const [pendingRequests, setPendingRequests] = useState<Friend[]>([]);
  const [sentRequests, setSentRequests] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [dialogPeople, setDialogPeople] = useState<UserProfile[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);

  const LIMIT = 5;

  const loadData = async (append: boolean = false) => {
    if (!user?.id) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setOffset(0);
    }

    try {
      if (!append) {
        const [requestsResult, sentResult] = await Promise.all([
          getPendingFriendRequests(user.id),
          getSentFriendRequests(user.id),
        ]);

        if (requestsResult.success && requestsResult.data) {
          setPendingRequests(requestsResult.data);
        }

        if (sentResult.success && sentResult.data) {
          setSentRequests(sentResult.data);
        }
      }

      const currentOffset = append ? offset : 0;
      const result = await getFriendRecommendations(
        user.id,
        LIMIT,
        currentOffset
      );

      if (result.success && result.data) {
        let filteredRecommendations = result.data;

        if (!append && pendingRequests.length > 0) {
          const pendingRequestUserIds = new Set(
            pendingRequests.map(req => req.user_id)
          );
          filteredRecommendations = result.data.filter(
            rec => !pendingRequestUserIds.has(rec.id)
          );
        }

        if (append) {
          setRecommendations(prev => [...prev, ...filteredRecommendations]);
        } else {
          setRecommendations(filteredRecommendations);
        }

        setHasMore(result.data.length === LIMIT);
        setOffset(prev => (append ? prev + LIMIT : LIMIT));
      } else {
        toast.error(result.error || "Gagal memuat rekomendasi teman");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Terjadi kesalahan saat memuat data");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadData(false);
  }, [user?.id]);

  const handleFriendAdded = async () => {
    if (user?.id) {
      try {
        const result = await getSentFriendRequests(user.id);
        if (result.success && result.data) {
          setSentRequests(result.data);
        }
      } catch (error) {
        console.error("Error reloading sent requests:", error);
      }
    }
  };

  const openSearch = async () => {
    setIsSearchDialogOpen(true);
    if (!user?.id) return;
    setDialogLoading(true);
    try {
      const PAGE = 50;
      let all: UserProfile[] = [];
      let off = 0;
      // ensure we have latest sent requests so pending status is accurate
      const sentResult = await getSentFriendRequests(user.id);
      if (sentResult.success && sentResult.data) {
        setSentRequests(sentResult.data);
      }
      // paginate until fewer than PAGE results
      // apply same filtering of users who have pending requests TO current user
      const pendingIds = new Set(pendingRequests.map(req => req.user_id));
      // fetch loop
      // safety cap: max 20 pages
      for (let i = 0; i < 20; i++) {
        const res = await getFriendRecommendations(user.id, PAGE, off);
        if (!res.success || !res.data) break;
        const batch = res.data.filter(rec => !pendingIds.has(rec.id));
        all = all.concat(batch);
        if (res.data.length < PAGE) break;
        off += PAGE;
      }
      setDialogPeople(all);
    } catch (e) {
      console.error("Error loading full recommendations:", e);
      toast.error("Gagal memuat semua rekomendasi");
    } finally {
      setDialogLoading(false);
    }
  };
  const closeSearch = () => setIsSearchDialogOpen(false);

  const handleRequestHandled = (requestId: string) => {
    setPendingRequests(prev => prev.filter(req => req.id !== requestId));
    loadData(false);
    if (onFriendAccepted) {
      onFriendAccepted();
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadData(true);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#373e46]">
            Rekomendasi Teman
          </h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-3/4 mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="w-12 h-6 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#373e46]">
            Rekomendasi Teman
          </h3>
          <Button
            onClick={openSearch}
            variant="ghost"
            size="sm"
            className="text-[#6b2cfe] hover:text-[#5a24d4] hover:bg-[#6b2cfe]/10 p-1 h-auto text-xs flex items-center gap-1"
          >
            <span>See All</span>
            <ExternalLinkIcon className="w-3 h-3" />
          </Button>
        </div>

        <div className="space-y-3">
          {pendingRequests.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-blue-800 mb-2">
                Permintaan Pertemanan
              </h4>
              {pendingRequests.map(request => (
                <FriendRequestItem
                  key={request.id}
                  request={request}
                  onRequestHandled={handleRequestHandled}
                  onFriendAccepted={onFriendAccepted}
                />
              ))}
              <div className="border-t border-gray-200 my-3"></div>
            </div>
          )}

          {recommendations.length > 0 ? (
            <div className="space-y-1">
              {recommendations.map(recommendation => (
                <FriendRecommendationItem
                  key={recommendation.id}
                  recommendation={recommendation}
                  sentRequests={sentRequests}
                  onFriendAdded={handleFriendAdded}
                />
              ))}

              {hasMore && (
                <div className="pt-3">
                  <Button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    variant="outline"
                    size="sm"
                    className="w-full text-[#6b2cfe] border-[#6b2cfe] hover:bg-[#6b2cfe] hover:text-white h-8 text-xs transition-all duration-300"
                  >
                    {loadingMore ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                        <span>Loading...</span>
                      </div>
                    ) : (
                      "Load More"
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : pendingRequests.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                <UsersIcon className="w-6 h-6 text-gray-400" />
              </div>
              <h4 className="text-sm font-medium text-[#373e46] mb-1">
                Tidak Ada Rekomendasi
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                Belum ada guru lain yang dapat direkomendasikan
              </p>
              <Button
                onClick={() => loadData(false)}
                size="sm"
                className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-4 py-1 rounded-lg font-medium transition-all duration-300 hover:shadow-lg text-xs h-7"
              >
                Refresh
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <SearchPeopleDialog
        isOpen={isSearchDialogOpen}
        onClose={closeSearch}
        people={dialogPeople.length ? dialogPeople : recommendations}
        sentRequests={sentRequests}
        isLoading={dialogLoading}
        onRefresh={() => openSearch()}
        onAdded={handleFriendAdded}
      />
    </>
  );
};
